import React from 'react'
import { Box, Divider, List, ListItem, Button, Pagination, Typography } from '@mui/material'

export interface WebinarSession {
  webinarId: string
  occurrenceId: string
  topic: string
  start_time: string
  duration: number
  join_url?: string
  registration_url?: string
  description?: string
  is_registered?: boolean
}

export interface WebinarType {
  id: string
  name: string
  nickname: string
  accessLevel: string
  description: string
  schedule: string
  webinarId?: string
}

export interface WebinarSessionsListProps {
  sessions: WebinarSession[]
  formatDateTime: (isoString: string) => string
  formatDuration: (minutes: number) => string
  page: number
  pageSize: number
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void
  isAllTab?: boolean
  allWebinarTypes?: WebinarType[]
}

const WebinarSessionsList: React.FC<WebinarSessionsListProps> = ({
  sessions,
  formatDateTime,
  formatDuration,
  page,
  pageSize,
  onPageChange,
  isAllTab = false,
  allWebinarTypes = [],
}) => {
  const startIndex = (page - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, sessions.length)
  const displayedSessions = sessions.slice(startIndex, endIndex)

  const totalPages = Math.max(1, Math.ceil(sessions.length / pageSize))

  const getWebinarDescription = (webinarId: string): string => {
    const webinarType = allWebinarTypes.find(
      (type) => type.id === webinarId || type.webinarId === webinarId
    )

    return webinarType?.description || 'No category description available'
  }

  const renderCategorySelectedItem = (session: WebinarSession, index: number) => (
    <React.Fragment key={session.occurrenceId || `session-${index}`}>
      <ListItem>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography
            variant="body1"
            component="span"
            fontWeight="medium"
            color="text.primary"
            sx={{ flexGrow: 1 }}
          >
            {session.topic}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="text.secondary"
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            {formatDateTime(session.start_time)} • {formatDuration(session.duration)}
          </Typography>
        </Box>
      </ListItem>
      {index < displayedSessions.length - 1 && <Divider />}
    </React.Fragment>
  )

  const renderAllTabItem = (session: WebinarSession, index: number) => {
    const now = new Date()
    const startTime = new Date(session.start_time)
    const thirtyMinutesInMs = 30 * 60 * 1000
    const canJoin = startTime.getTime() - now.getTime() <= thirtyMinutesInMs

    let buttonElement = null

    if (canJoin && session.join_url) {
      buttonElement = (
        <Button
          variant="contained"
          color="primary"
          size="small"
          href={session.join_url}
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ml: 2, whiteSpace: 'nowrap' }}
        >
          Join
        </Button>
      )
    }

    const webinarDescription = getWebinarDescription(session.webinarId)

    return (
      <React.Fragment key={session.occurrenceId || `session-${index}`}>
        <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography
              variant="body1"
              component="span"
              fontWeight="medium"
              color={'text.primary'}
              sx={{ flexGrow: 1 }}
            >
              {session.topic}
            </Typography>

            {buttonElement}
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            width="100%"
            mt={1}
          >
            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              sx={{ flexGrow: 1, pr: 2 }}
            >
              {webinarDescription}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {formatDateTime(session.start_time)} • {formatDuration(session.duration)}
            </Typography>
          </Box>
        </ListItem>
        {index < displayedSessions.length - 1 && <Divider />}
      </React.Fragment>
    )
  }

  return (
    <>
      <List dense>
        {displayedSessions.map((session, index) =>
          isAllTab ? renderAllTabItem(session, index) : renderCategorySelectedItem(session, index)
        )}
      </List>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
            size="small"
            siblingCount={1}
            boundaryCount={0}
          />
        </Box>
      )}
    </>
  )
}

export default WebinarSessionsList
